import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, timeAgo } from "@/lib/format";
import { toast } from "sonner";

const STEP = 100;
const field = { background: "transparent", border: "1px solid var(--border)", color: "var(--text-primary)" };

function StatusTag({ status }) {
  const filled = status === "paid";
  return (
    <span className="text-[10px] uppercase tracking-widest px-2 py-1" style={{ border: "1px solid var(--accent)", color: filled ? "#121212" : "var(--accent)", background: filled ? "var(--accent)" : "transparent" }}>
      {status}
    </span>
  );
}

function MyCashouts({ items }) {
  if (!items.length) return null;
  return (
    <ul className="mt-8 flex flex-col gap-2" data-testid="my-cashouts">
      {items.map((c) => (
        <li key={c.id} data-testid={`my-cashout-${c.id}`} className="flex items-center justify-between gap-3 px-4 py-3 text-xs" style={{ border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
          <span>{c.amount} provisions · ${c.amount}.00 CAD · {timeAgo(c.created_at)}</span>
          <StatusTag status={c.status} />
        </li>
      ))}
    </ul>
  );
}

export function CashOut() {
  const { user, refreshUser } = useAuth();
  const [amount, setAmount] = useState(STEP);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const balance = user?.provisions ?? 0;
  const maxSteps = Math.floor(balance / STEP);

  const loadMine = useCallback(() => api.get("/cashouts/mine").then(({ data }) => setMine(data)).catch((e) => console.debug("cashouts", e)), []);
  useEffect(() => { if (!user?.is_guest && !user?.is_god) loadMine(); }, [loadMine, user?.is_guest, user?.is_god]);

  if (user?.is_guest || user?.is_god) return null;

  const valid = maxSteps >= 1 && amount >= STEP && amount <= maxSteps * STEP && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await api.post("/cashouts", { amount, payout_email: email.trim() });
      toast.success("cash out requested");
      setAmount(STEP);
      await refreshUser?.();
      loadMine();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-16 pt-10" style={{ borderTop: "1px solid var(--border)" }} data-testid="cashout-section">
      <h2 className="font-head text-xl mb-2" style={{ color: "var(--text-primary)" }}>Cash out</h2>
      <p className="text-xs" style={{ color: "var(--text-muted)" }} data-testid="cashout-desc-rate">100 at a time · 1 provision = 1 CAD</p>
      <p className="text-xs mt-1 mb-6" style={{ color: "var(--text-muted)" }} data-testid="cashout-desc-gods">paid by the members</p>
      {maxSteps < 1 ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="cashout-min-msg">you need at least 100 provisions to cash out</p>
      ) : (
        <>
          <label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>amount</label>
          <div className="flex items-center gap-3 mt-2">
            <button onClick={() => setAmount((a) => Math.max(STEP, a - STEP))} disabled={amount <= STEP} data-testid="cashout-minus-btn" className="btn w-11 h-11 text-lg">−</button>
            <span className="flex-1 text-center font-head text-xl py-2" style={field} data-testid="cashout-amount">{amount}</span>
            <button onClick={() => setAmount((a) => Math.min(maxSteps * STEP, a + STEP))} disabled={amount >= maxSteps * STEP} data-testid="cashout-plus-btn" className="btn w-11 h-11 text-lg">+</button>
          </div>
          <label className="text-xs uppercase tracking-widest block mt-6" style={{ color: "var(--text-muted)" }}>interac e-transfer / paypal email</label>
          <input data-testid="cashout-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 mt-2" style={field} />
          <p className="font-head text-xl mt-6" style={{ color: "var(--text-primary)" }} data-testid="cashout-total">you receive · ${amount}.00 CAD</p>
          <button onClick={submit} disabled={!valid || busy} data-testid="cashout-submit-btn" className="btn w-full py-3 mt-6 text-sm tracking-widest">{busy ? "…" : "CASH OUT"}</button>
        </>
      )}
      <MyCashouts items={mine} />
    </div>
  );
}

function CashoutRow({ c, onDone }) {
  const [busy, setBusy] = useState(false);
  const act = async (kind) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/cashouts/${c.id}/${kind}`);
      toast.success(kind === "paid" ? "marked paid" : "rejected · provisions returned");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };
  return (
    <li data-testid={`god-cashout-${c.id}`} className="px-4 py-4" style={{ border: "1px solid var(--border)" }}>
      <div className="flex items-center justify-between gap-3">
        <span className="font-head text-lg" style={{ color: "var(--text-primary)" }}>${c.amount}.00 CAD</span>
        <StatusTag status={c.status} />
      </div>
      <p className="text-xs mt-2" style={{ color: "var(--text-secondary)" }}>{c.user_name || "unknown"} · {timeAgo(c.created_at)}</p>
      <p className="text-sm mt-1 break-all" style={{ color: "var(--accent)" }} data-testid={`god-cashout-email-${c.id}`}>{c.payout_email}</p>
      {c.status === "pending" && (
        <div className="flex gap-3 mt-4">
          <button onClick={() => act("reject")} disabled={busy} data-testid={`god-cashout-reject-${c.id}`} className="btn flex-1 py-2 text-xs tracking-widest">REJECT</button>
          <button onClick={() => act("paid")} disabled={busy} data-testid={`god-cashout-paid-${c.id}`} className="btn flex-1 py-2 text-xs tracking-widest">MARK PAID</button>
        </div>
      )}
    </li>
  );
}

export function GodCashouts() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/cashouts").then(({ data }) => setItems(data)).catch((e) => console.debug("god cashouts", e)), []);
  useEffect(() => { if (user?.is_god) load(); }, [load, user?.is_god]);
  if (!user?.is_god) return null;
  return (
    <div className="mt-16 pt-10" style={{ borderTop: "1px solid var(--border)" }} data-testid="god-cashouts">
      <h2 className="font-head text-xl mb-6" style={{ color: "var(--text-primary)" }}>Cash outs</h2>
      {!items ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</p>
      ) : !items.length ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="god-cashouts-empty">No cash out requests.</p>
      ) : (
        <ul className="flex flex-col gap-3">{items.map((c) => <CashoutRow key={c.id} c={c} onDone={load} />)}</ul>
      )}
    </div>
  );
}
